import { Component } from 'react';
import { CoralMark } from './CoralMark.jsx';

// Catches any render-time exception below it so a crash shows a
// visible "something broke" surface instead of a white page. Includes
// a reload button (often resolves stale-bundle issues after a deploy)
// and a copyable error message for feedback.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    try {
      console.error('[ErrorBoundary]', error, info?.componentStack);
    } catch {
      /* ignore */
    }
    // Fire-and-forget POST so admin gets a real signal — the white-page
    // bug a user reported on 6/7 had no console traceability. keepalive
    // lets the request finish even if the user reloads the tab.
    try {
      const payload = {
        // Never the fragment: the sitter page keeps its token there.
        url: window.location.origin + window.location.pathname + window.location.search,
        message: error?.message || String(error),
        stack: error?.stack || null,
        componentStack: info?.componentStack || null,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        buildId:
          document.querySelector('script[src*="/assets/index-"]')?.getAttribute('src') || null,
      };
      fetch('/api/client-error', {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch {
      /* never throw from the boundary itself */
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          color: 'var(--text-muted)',
          background: 'var(--bg)',
        }}
      >
        <div
          style={{
            maxWidth: 420,
            background: 'var(--surface)',
            border: '1px solid var(--surface-border)',
            borderRadius: 18,
            padding: '28px 24px',
            textAlign: 'center',
          }}
        >
          <CoralMark size={40} style={{ marginBottom: 2 }} />
          <div
            className="font-heading"
            style={{
              fontSize: 11,
              letterSpacing: '0.3em',
              color: 'var(--text-dim)',
              marginBottom: 10,
            }}
          >
            SENSEI
          </div>
          <h1
            className="font-heading"
            style={{ fontSize: 20, color: 'var(--text)', margin: '0 0 8px' }}
          >
            Sensei tripped on something.
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, margin: '0 0 14px' }}>
            The app hit an unexpected error. Reloading usually fixes it after a fresh deploy.
          </p>
          <pre
            style={{
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              fontSize: 13,
              color: 'var(--alert)',
              background: 'rgba(0,0,0,0.18)',
              padding: '8px 10px',
              borderRadius: 8,
              overflow: 'auto',
              textAlign: 'left',
              margin: '0 0 16px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {msg}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="font-heading"
            style={{
              padding: '10px 18px',
              borderRadius: 999,
              background: 'var(--cyan)',
              color: 'var(--cyan-text)',
              fontSize: 13,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
